import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryIcon from "@mui/icons-material/Category";
import { useTranslation } from "react-i18next";
import { useManagePage } from "@hooks/useManagePage";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { PageContainer } from "@components/PageContainer";
import { PageHeader } from "@components/PageHeader";
import { BRAND } from "@/constants/brand";

export default function Manage() {
  const { t } = useTranslation();
  const {
    manageKind,
    setManageKind,
    categoriesForKind,
    catDialogOpen,
    closeCatDialog,
    editingCat,
    catForm,
    setCatForm,
    subDialogOpen,
    closeSubDialog,
    subForm,
    setSubForm,
    deleteTarget,
    setDeleteTarget,
    openEditCat,
    saveCat,
    openCreateSub,
    saveSub,
    handleDelete,
    exportCategoriesCsv,
    categoriesCsvInputRef,
    categoriesCsvImporting,
    onManageCategoriesCsvSelected,
    pickManageCategoriesCsvFile,
  } = useManagePage();

  return (
    <PageContainer>
      <input
        ref={categoriesCsvInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={onManageCategoriesCsvSelected}
      />
      <PageHeader title={t("manage.title")} subtitle={t("manage.subtitle")} />
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          exclusive
          value={manageKind}
          onChange={(_, next) => next != null && setManageKind(next)}
          size="small"
          color="secondary"
          aria-label={t("manage.kindFilterLabel")}
        >
          <ToggleButton value="expense">
            {t("manage.expenseCategories")}
          </ToggleButton>
          <ToggleButton value="gain">{t("manage.gainCategories")}</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={exportCategoriesCsv}
          disabled={categoriesCsvImporting}
        >
          {t("manage.exportCategoriesCsv")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileUploadOutlinedIcon />}
          onClick={pickManageCategoriesCsvFile}
          disabled={categoriesCsvImporting}
        >
          {t("manage.importCategoriesCsv")}
        </Button>
      </Box>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {categoriesForKind.map((cat) => (
              <Card key={cat.id} elevation={0} sx={{ borderRadius: "12px" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          bgcolor: BRAND.peachTint,
                          color: BRAND.orange,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CategoryIcon />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: 18,
                          }}
                        >
                          {cat.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {cat.subcategories?.length || 0}{" "}
                          {t("manage.subcategories")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => openEditCat(cat)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setDeleteTarget({ type: "category", id: cat.id })
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {cat.subcategories?.map((sub) => (
                      <Chip
                        key={sub.id}
                        label={sub.name}
                        size="small"
                        variant="outlined"
                        onDelete={() =>
                          setDeleteTarget({ type: "subcategory", id: sub.id })
                        }
                      />
                    ))}
                    <Chip
                      icon={<AddIcon />}
                      label={t("manage.addSubcategory")}
                      size="small"
                      variant="outlined"
                      onClick={() => openCreateSub(cat.id)}
                      sx={{ borderStyle: "dashed", cursor: "pointer" }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
            {categoriesForKind.length === 0 && (
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  py: 4,
                }}
              >
                {t("common.noData")}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              bgcolor: "#FFE5D930",
              border: "1px solid #FFE5D9",
              p: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontFamily: "'Playfair Display', serif",
                mb: 3,
              }}
            >
              {manageKind === "gain"
                ? t("manage.createGainCategory")
                : t("manage.createExpenseCategory")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label={t("manage.categoryName")}
                value={catForm.name}
                onChange={(e) =>
                  setCatForm({ ...catForm, name: e.target.value })
                }
                fullWidth
                size="small"
              />
              <TextField
                label={t("manage.description")}
                value={catForm.description}
                onChange={(e) =>
                  setCatForm({ ...catForm, description: e.target.value })
                }
                fullWidth
                size="small"
              />
              <Button
                variant="contained"
                fullWidth
                onClick={saveCat}
                disabled={!catForm.name}
              >
                {t("manage.addCategory")}
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>
      <Dialog
        open={catDialogOpen && !!editingCat}
        onClose={closeCatDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("common.edit")}</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "16px !important",
          }}
        >
          <TextField
            label={t("manage.categoryName")}
            value={catForm.name}
            onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
            fullWidth
          />
          <TextField
            label={t("manage.description")}
            value={catForm.description}
            onChange={(e) =>
              setCatForm({ ...catForm, description: e.target.value })
            }
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCatDialog}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={saveCat}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={subDialogOpen}
        onClose={closeSubDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("manage.addSubcategory")}</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "16px !important",
          }}
        >
          <TextField
            label={t("manage.subcategoryName")}
            value={subForm.name}
            onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
            fullWidth
          />
          <TextField
            label={t("manage.description")}
            value={subForm.description}
            onChange={(e) =>
              setSubForm({ ...subForm, description: e.target.value })
            }
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSubDialog}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={saveSub}
            disabled={!subForm.name}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("common.delete")}
        message={
          deleteTarget?.type === "category"
            ? t("manage.deleteCategoryConfirm")
            : t("manage.deleteSubcategoryConfirm")
        }
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageContainer>
  );
}
